import React from "react";
import {
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  List,
  ListItemText,
  Typography,
  DialogTitle,
  IconButton,
  Button,
  Box,
} from "@mui/material";
import { ReturnButton } from "components/ReturnButton";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { useAuth } from "shared/providers/auth";
import * as Styled from "./styles";
import { getPhysical } from "requests/RecordPhysical";
import { IFile } from "shared/types/RecordPhysical";
import { MdClose } from "react-icons/md";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import { getPdf } from "requests/File";

import { Document, Page, pdfjs } from "react-pdf";

import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const RecordPhysical: React.FC<{
  idPacientParam?: number;
  onClick?: () => void;
}> = ({ idPacientParam, onClick }) => {
  const { user } = useAuth();
  const { idPacient } = useParams();

  const [loading, setLoading] = React.useState<boolean>(true);
  const [physical, setPhysical] = React.useState<IFile[]>([]);
  const [selectedFile, setSelectedFile] = React.useState<IFile>({} as IFile);
  const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = React.useState<boolean>(true);

  const [pdfData, setPdfData] = React.useState<string | null>(null);

  const [numPages, setNumPages] = React.useState<number>(0);

  const [pdfBlob, setPdfBlob] = React.useState<Blob | null>(null);

  const dialogRef = React.useRef<HTMLDivElement>(null);

  const fetchPhysicalRecords = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { status, data } = await getPhysical({
        userId: user.data.id,
        idPac: Number(
          idPacientParam && idPacientParam > 0 ? idPacientParam : idPacient
        ),
      });

      if ([200, 201].includes(status)) {
        setPhysical(data.data);
      } else {
        setError(data.message || "Erro ao carregar registros");
        toast.error(data.message || "Erro ao carregar registros");
      }
    } catch (e: any) {
      setError("Falha na conexão com o servidor");
      toast.error("Falha na conexão com o servidor");
    } finally {
      setLoading(false);
    }
  }, [idPacient, idPacientParam, user]);

  const fetchPdf = async (file: IFile) => {
    setIsDialogOpen(true);
    setPdfLoading(true);
    setPdfBlob(null);
    setPdfData(null);
    setSelectedFile(file);

    try {
      const { data } = await getPdf({
        file: file,
        userId: user.data.id,
        idPac: Number(idPacientParam ?? idPacient),
      });

      if (!(data instanceof Blob)) {
        throw new Error("Arquivo inválido ou corrompido");
      }

      const url = URL.createObjectURL(data);
      setPdfBlob(data);
      setPdfData(url);
    } catch (error) {
      console.error("Erro ao carregar o PDF:", error);
      setError("Falha ao carregar o arquivo PDF.");
      toast.error("Falha ao carregar o PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setPdfBlob(null);
    setPdfData(null);
    setNumPages(0);
    setError(null);
  };

  const handleDownload = async (url: string | Blob, filename: string) => {
    try {
      // Converte Blob para base64
      const convertBlobToBase64 = (blob: Blob): Promise<string> =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

      const isNative = (window as any).ReactNativeWebView;

      if (isNative) {
        // Se for Native WebView
        let base64String =
          typeof url === "string" ? url : await convertBlobToBase64(url);

        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify({
            action: "downloadBase64File",
            value: base64String,
            filename,
          })
        );
      } else {
        // Se for navegador, baixar diretamente
        const blobUrl = url instanceof Blob ? URL.createObjectURL(url) : url;

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Libera memória se foi um blob
        if (url instanceof Blob) {
          URL.revokeObjectURL(blobUrl);
        }
      }
    } catch (e) {
      console.error("Erro ao baixar o arquivo:", e);
      toast.error("Erro ao baixar o arquivo");
    }
  };

  React.useEffect(() => {
    fetchPhysicalRecords();
  }, [fetchPhysicalRecords]);

  return (
    <Container maxWidth="xl">
      <ReturnButton title="Ficha Clínica Física" onClick={onClick} />

      {loading ? (
        <Styled.LoadingContainer>
          <CircularProgress />
          <Typography>Carregando registros...</Typography>
        </Styled.LoadingContainer>
      ) : error ? (
        <Styled.ErrorContainer>
          <Typography color="error">{error}</Typography>
        </Styled.ErrorContainer>
      ) : (
        <Styled.ContainerPictures>
          {physical.length > 0 ? (
            <List>
              {physical.map((file, index) => (
                <Styled.StyledListItem
                  key={`${file.id}-${index}`}
                  secondaryAction={
                    <Styled.BoxButtons>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          fetchPdf(file);
                        }}
                      >
                        Visualizar
                      </Button>
                    </Styled.BoxButtons>
                  }
                >
                  <ListItemText
                    primary={
                      file.original_name.length > 30
                        ? `${file.original_name.slice(0, 30)}...`
                        : file.original_name
                    }
                    secondary={`${file.size_formatted} - ${file.upload_date_formatted}`}
                  />
                </Styled.StyledListItem>
              ))}
            </List>
          ) : (
            <Typography>Não existem registros físicos.</Typography>
          )}
        </Styled.ContainerPictures>
      )}

      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Styled.BoxTitle>
            {selectedFile &&
            selectedFile.original_name &&
            selectedFile.original_name.length > 30
              ? `${selectedFile.original_name.slice(0, 30)}...`
              : selectedFile.original_name}

            <Button
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                if (pdfBlob) {
                  handleDownload(pdfBlob, selectedFile.original_name);
                } else {
                  toast.error("Arquivo ainda não carregado.");
                }
              }}
            >
              Baixar
            </Button>
          </Styled.BoxTitle>
          <IconButton
            aria-label="close"
            onClick={() => setIsDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <MdClose />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers ref={dialogRef}>
          {selectedFile && (
            <Styled.PDFContainer>
              {!pdfLoading && !pdfData && (
                <Box textAlign="center" p={4}>
                  <Typography color="error">
                    Falha ao carregar o PDF. Tente novamente.
                  </Typography>
                </Box>
              )}

              {pdfData && (
                <TransformWrapper
                  initialScale={1}
                  minScale={0.6}
                  maxScale={4}
                  doubleClick={{ disabled: false }}
                  pinch={{ disabled: false }}
                  panning={{ disabled: false }}
                >
                  <TransformComponent
                    wrapperStyle={{
                      height: "80vh",
                      overflow: "auto",
                    }}
                    contentStyle={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Document
                      file={pdfData}
                      onLoadSuccess={({ numPages }) => {
                        setNumPages(numPages);
                        setPdfLoading(false);
                      }}
                      onLoadError={(err) => {
                        console.error("Erro ao carregar o documento PDF", err);
                        setPdfLoading(false);
                        setPdfData(null);
                        toast.error("Erro ao renderizar o PDF.");
                      }}
                      loading={<CircularProgress />}
                    >
                      {Array.from({ length: numPages }, (_, index) => (
                        <Page
                          key={`page_${index + 1}`}
                          pageNumber={index + 1}
                          onRenderError={(error) => {
                            console.error(
                              `Erro ao renderizar a página ${index + 1}`,
                              error
                            );
                            toast.error(`Erro na página ${index + 1} do PDF.`);
                          }}
                        />
                      ))}
                    </Document>
                  </TransformComponent>
                </TransformWrapper>
              )}
            </Styled.PDFContainer>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export { RecordPhysical };
